import { motion } from "framer-motion";
import {
    FiBook,
    FiPlay,
    FiCode,
    FiCheckCircle,
    FiArrowRight,
} from "react-icons/fi";
import { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";

const Guide = () => {
    const { t } = useLanguage();
    const [modalOpen, setModalOpen] = useState(false);
    const guides = [
        {
            id: 1,
            title: t("guide.minddeck.title"),
            description: t("guide.minddeck.desc"),
            steps: [
                t("guide.minddeck.step1"),
                t("guide.minddeck.step2"),
                t("guide.minddeck.step3"),
                t("guide.minddeck.step4"),
                t("guide.minddeck.step5"),
            ],
            icon: FiBook,
            color: "from-purple-500 to-pink-500",
        },
        {
            id: 2,
            title: t("guide.jsonparser.title"),
            description: t("guide.jsonparser.desc"),
            steps: [
                t("guide.jsonparser.step1"),
                t("guide.jsonparser.step2"),
                t("guide.jsonparser.step3"),
                t("guide.jsonparser.step4"),
                t("guide.jsonparser.step5"),
            ],
            icon: FiCode,
            color: "from-blue-500 to-cyan-500",
        },
    ];

    const tips = [
        {
            title: t("guide.tip1.title"),
            description: t("guide.tip1.desc"),
            icon: FiCheckCircle,
        },
        {
            title: t("guide.tip2.title"),
            description: t("guide.tip2.desc"),
            icon: FiCheckCircle,
        },
        {
            title: t("guide.tip3.title"),
            description: t("guide.tip3.desc"),
            icon: FiCheckCircle,
        },
        {
            title: t("guide.tip4.title"),
            description: t("guide.tip4.desc"),
            icon: FiCheckCircle,
        },
    ];

    return (
        <div className="py-12 space-y-16">
            {/* Header */}
            <section className="text-center space-y-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-2xl mb-6">
                        <FiBook className="w-8 h-8" />
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent mb-6">
                        {t("guide.title")}
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
                        {t("guide.subtitle")}
                    </p>
                </motion.div>
            </section>

            {/* Guides */}
            <section className="space-y-12">
                {guides.map((guide, index) => (
                    <motion.div
                        key={guide.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.2 }}
                        viewport={{ once: true }}
                        className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                        <div className="p-8 md:p-12">
                            <div className="flex items-start space-x-6">
                                <div
                                    className={`flex-shrink-0 w-16 h-16 bg-gradient-to-r ${guide.color} text-white rounded-2xl flex items-center justify-center`}
                                >
                                    <guide.icon className="w-8 h-8" />
                                </div>
                                <div className="flex-1">
                                    <h2 className="text-2xl font-bold mb-4">
                                        {guide.title}
                                    </h2>
                                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                                        {guide.description}
                                    </p>

                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold">
                                            {t("guide.stepsTitle")}
                                        </h3>
                                        <ol className="space-y-3">
                                            {guide.steps.map(
                                                (step, stepIndex) => (
                                                    <li
                                                        key={stepIndex}
                                                        className="flex items-start space-x-3"
                                                    >
                                                        <div className="flex-shrink-0 w-6 h-6 bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center text-sm font-semibold">
                                                            {stepIndex + 1}
                                                        </div>
                                                        <span className="text-gray-700 dark:text-gray-300">
                                                            {step}
                                                        </span>
                                                    </li>
                                                )
                                            )}
                                        </ol>
                                    </div>

                                    <div className="mt-8">
                                        <button
                                            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
                                            onClick={() => setModalOpen(true)}
                                        >
                                            <FiPlay className="w-5 h-5" />
                                            <span>{t("common.learnMore")}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </section>

            {/* Tips Section */}
            <section className="space-y-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center"
                >
                    <h2 className="text-3xl font-bold mb-4">
                        {t("guide.tipsTitle")}
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-400">
                        {t("guide.tipsDesc")}
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-6">
                    {tips.map((tip, index) => (
                        <motion.div
                            key={tip.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700"
                        >
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 rounded-xl flex items-center justify-center">
                                    <tip.icon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2">
                                        {tip.title}
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        {tip.description}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section className="text-center space-y-8 py-16 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-3xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-3xl font-bold mb-4">
                        {t("guide.ctaTitle")}
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                        {t("guide.ctaDesc")}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <a
                            href="https://minddeck.mewseeds.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
                        >
                            <span>{t("guide.cta.minddeck")}</span>
                            <FiArrowRight className="w-5 h-5" />
                        </a>
                        <a
                            href="https://json.mewseeds.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
                        >
                            <span>{t("guide.cta.jsonparser")}</span>
                            <FiArrowRight className="w-5 h-5" />
                        </a>
                    </div>
                </motion.div>
            </section>

            {/* 모달 */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-10 shadow-xl max-w-md w-full text-center">
                        <h3 className="text-lg font-bold mb-4">
                            {t("guide.modalTitle")}
                        </h3>
                        <p className="mb-6 text-gray-700 dark:text-gray-200">
                            {t("guide.modalDesc")}
                        </p>
                        <button
                            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                            onClick={() => setModalOpen(false)}
                        >
                            {t("common.close")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Guide;
